import { catchError, Observable } from "rxjs";
import { ServiceHandler } from "@nucleus/ui-base";
import { Injectable } from "@angular/core";
import { ErrorHandlerService } from "./error-handler.service";
import { OverlayService } from '@nucleus/ui-common';


@Injectable({
  providedIn: 'root'
})
export class RequestHandler {
  constructor(
    private errorHandlerService: ErrorHandlerService,
    private overlayService: OverlayService) { }

     /** @param context  Optional human label of the operation being performed
      *                  ("loading locations", "saving user", …). When set,
      *                  the error toast/dialog reads "Loading locations:
      *                  Something went wrong on our end…" instead of just
      *                  the generic message — gives the user actionable
      *                  context about what failed. */
     public handleRequest<T>( source$: Observable<T>, handler: ServiceHandler<T>, blocking: boolean = false, context?: string): void {
       if (blocking) this.overlayService.show();
       handler.onStart();

       source$
         .pipe(catchError(error => this.errorHandlerService.handleError(error)))
         .subscribe({
           next: (value) => {
              handler.onSuccess(value);
           },
           error: (error) => {
             handler.onError(RequestHandler.friendlyMessage(error, context));
             handler.onComplete();
             if (blocking) this.overlayService.hide();
           },
           complete: () => {
             handler.onComplete();
             if (blocking) this.overlayService.hide();
           }
         });
     }

     /** Map any error into a clean, user-facing string. Status codes drive the
      *  copy — we never propagate raw backend descriptions ("Malformed Request
      *  Body", stack traces, etc.) to the UI. The full diagnostic is logged to
      *  the console by error-handler.service.ts. When `context` is provided,
      *  it is sentence-cased and prepended so the user sees WHAT failed, not
      *  just a generic "something went wrong". */
     private static friendlyMessage(error: any, context?: string): string {
       const base = RequestHandler.baseMessage(error);
       if (!context) return base;
       const head = context.charAt(0).toUpperCase() + context.slice(1);
       return `${head}: ${base}`;
     }

     private static baseMessage(error: any): string {
       if (typeof error === 'string') return error;
       const status = error?.status ?? 0;
       if (status === 0)   return 'Connection problem. Check your network and try again.';
       if (status === 400) return "We couldn't process that request. Please try again or contact support.";
       if (status === 401) return 'Your session may have expired. Please refresh or log in again.';
       if (status === 403) return "You don't have permission to perform this action.";
       if (status === 404) return "We couldn't find what you were looking for.";
       if (status === 409) return 'This action conflicts with the current state. Please refresh and try again.';
       if (status === 422) return "The information provided isn't valid. Please review and try again.";
       if (status === 429) return 'Too many requests. Please slow down and try again shortly.';
       if (status === 503) return 'Service is temporarily unavailable. Please try again shortly.';
       if (status >= 500)  return 'Something went wrong on our end. Please try again or contact support.';
       return 'An unexpected error occurred. Please try again.';
     }
}
